import { apiService, authApiService } from '..';

import { APIConstants } from '../../constants/api';
import { ApplicationId, ParentApplicationId } from '../../constants/global';
import { APIRequestMethods } from '../../constants/network';
import AuthTokenManager from './token';
import {
  AuthAccessInterface,
  AuthAccessResponse,
  AuthFetchTokenInterface,
  AuthForgotPasswordInterface,
  AuthGetUserByCodeAPIResponseInterface,
  AuthGetUserByCodeInterface,
  AuthIsLoggedInInterface,
  AuthLoginAPIResponceInterface,
  AuthLoginInterface,
  AuthLogoutInterface,
  AuthResetPasswordInterface,
  AuthSendOTPInterface,
  AuthVerifyUserAPIResponseInterface,
  AuthVerifyUserInterface,
} from './types';

class AuthService extends AuthTokenManager {
  public static getAuthToken: AuthFetchTokenInterface = () => {
    const currentToken = AuthTokenManager.getToken();
    return currentToken && currentToken.length ? currentToken : null;
  };

  public static isLoggedIn: AuthIsLoggedInInterface = () => {
    const currentToken = AuthService.getAuthToken();
    return !!(currentToken && currentToken.length > 0);
  };

  public static expireToken = () => {
    AuthTokenManager.expireToken();
  };

  public static getUserByCode: AuthGetUserByCodeInterface = (data) => {
    return apiService
      .get<AuthGetUserByCodeAPIResponseInterface>({
        path: APIConstants.AUTH.GET_USER_BY_CODE,
        params: {
          ...data,
          parentApplicationId: ParentApplicationId ?? '',
        },
        isProtected: false,
      })
      .then((response) => {
        const { data } = response;
        return data;
      })
      .catch((error) => {
        throw error;
      });
  };

  public static verifyUser: AuthVerifyUserInterface = (data) => {
    return apiService
      .post<AuthVerifyUserAPIResponseInterface>({
        path: APIConstants.AUTH.VERIFY,
        data: { ...data },
        isProtected: false,
      })
      .then((response) => {
        const { data } = response;
        return data;
      })
      .catch((error) => {
        throw error;
      });
  };

  public static login: AuthLoginInterface = (data) => {
    return apiService
      .post<AuthLoginAPIResponceInterface>({
        path: APIConstants.AUTH.LOGIN,
        data: { ...data },
        params: {
          applicationId: ApplicationId ?? '',
        },
        isProtected: false,
      })
      .then((response) => {
        const { data } = response;
        AuthTokenManager.setToken(data.jwtToken);
        return data;
      })
      .catch((error) => {
        throw error;
      });
  };

  public static forgotPassword: AuthForgotPasswordInterface = (data) => {
    return apiService
      .post<string>({
        path: APIConstants.AUTH.FORGOT_PASSWORD,
        data: { ...data },
        params: {
          applicationId: ApplicationId ?? '',
          parentApplicationId: ParentApplicationId ?? '',
        },
        isProtected: false,
      })
      .then((response) => {
        return 'success';
      })
      .catch((error) => {
        throw error;
      });
  };

  public static resetPassword: AuthResetPasswordInterface = (data) => {
    return apiService
      .post<string>({
        path: APIConstants.AUTH.RESET_PASSWORD,
        data: {
          newPassword: data.newPassword,
        },
        params: {
          applicationId: ApplicationId ?? '',
          parentApplicationId: ParentApplicationId ?? '',
          code: data.code,
        },
        isProtected: false,
      })
      .then((response) => {
        return 'success';
      })
      .catch((error) => {
        throw error;
      });
  };

  public static sendOTP: AuthSendOTPInterface = (data) => {
    return apiService
      .post<string>({
        path: APIConstants.AUTH.SEND_OTP,
        data: { ...data },
        isProtected: false,
      })
      .then((response) => {
        const { data } = response;
        return data;
      })
      .catch((error) => {
        throw error;
      });
  };

  public static logout: AuthLogoutInterface = () => {
    return apiService
      .post<string>({
        path: APIConstants.AUTH.LOGOUT,
        params: {
          applicationId: ApplicationId ?? '',
        },
      })
      .then((response) => {
        const { data } = response;
        AuthTokenManager.expireToken();
        return data;
      })
      .catch((error) => {
        throw error;
      });
  };

  public static getUserAccess: AuthAccessInterface = () => {
    return authApiService
      .request({
        method: APIRequestMethods.GET,
        url: APIConstants.AUTH.GET_ACCESS_DATA,
        params: {
          applicationId: ApplicationId ?? '',
        },
      })
      .then((response) => {
        const { data } = response;
        return data as AuthAccessResponse;
      })
      .catch((error) => {
        throw error;
      });
  };
}

export default AuthService;
